<script setup>
/**
 * Blocked contacts.
 *
 * A settings-side review surface for the `blocked` flag that lives on the
 * contact record. Blocking still happens on the contact page; this page lists
 * everyone the workspace has blocked and offers the way back out. It talks to
 * the contacts filter API directly instead of the contacts store so browsing
 * the list never clobbers the Contacts area's own list state.
 */
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';

import ContactAPI from 'dashboard/api/contacts';
import Avatar from 'next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';

const { t } = useI18n();
const route = useRoute();

const contacts = ref([]);
const totalCount = ref(0);
const page = ref(1);
const isLoading = ref(true);
const isLoadingMore = ref(false);
const unblockingIds = ref({});

const BLOCKED_FILTER = {
  payload: [
    {
      attribute_key: 'blocked',
      filter_operator: 'equal_to',
      values: ['true'],
      attribute_model: 'standard',
      custom_attribute_type: '',
    },
  ],
};

const hasMore = computed(() => contacts.value.length < totalCount.value);

const fetchPage = async requestedPage => {
  const {
    data: { payload, meta },
  } = await ContactAPI.filter(requestedPage, 'name', BLOCKED_FILTER);
  totalCount.value = meta.count;
  return payload;
};

const loadFirstPage = async () => {
  isLoading.value = true;
  try {
    contacts.value = await fetchPage(1);
    page.value = 1;
  } finally {
    isLoading.value = false;
  }
};

const loadMore = async () => {
  isLoadingMore.value = true;
  try {
    const nextPage = page.value + 1;
    const payload = await fetchPage(nextPage);
    const knownIds = new Set(contacts.value.map(contact => contact.id));
    contacts.value = [
      ...contacts.value,
      ...payload.filter(contact => !knownIds.has(contact.id)),
    ];
    page.value = nextPage;
  } finally {
    isLoadingMore.value = false;
  }
};

const contactRoute = contact => ({
  name: 'contacts_edit',
  params: { accountId: route.params.accountId, contactId: contact.id },
});

const unblockContact = async contact => {
  unblockingIds.value = { ...unblockingIds.value, [contact.id]: true };
  try {
    await ContactAPI.update(contact.id, { blocked: false });
    contacts.value = contacts.value.filter(item => item.id !== contact.id);
    totalCount.value = Math.max(totalCount.value - 1, 0);
    useAlert(t('BLOCKED_CONTACTS.UNBLOCK.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('BLOCKED_CONTACTS.UNBLOCK.ERROR_MESSAGE'));
  } finally {
    unblockingIds.value = { ...unblockingIds.value, [contact.id]: false };
  }
};

onMounted(loadFirstPage);
</script>

<template>
  <SettingsPageShell
    wide
    :title="$t('BLOCKED_CONTACTS.HEADER')"
    :description="$t('BLOCKED_CONTACTS.DESCRIPTION')"
  >
    <template #actions>
      <span v-if="totalCount" class="text-sm text-n-slate-11">
        {{ $t('BLOCKED_CONTACTS.COUNT', { n: totalCount }) }}
      </span>
    </template>

    <div
      v-if="isLoading"
      role="status"
      aria-busy="true"
      :aria-label="$t('BLOCKED_CONTACTS.LOADING')"
      class="flex flex-col gap-5"
    >
      <Skeleton
        v-for="row in 4"
        :key="row"
        variant="text"
        :lines="2"
        :announce="false"
      />
    </div>

    <div
      v-else-if="!contacts.length"
      class="flex min-h-80 flex-col items-center justify-center gap-4 rounded-xl border border-n-weak bg-n-solid-1 px-6 py-16 text-center"
    >
      <span
        class="flex size-12 items-center justify-center rounded-full bg-n-alpha-2"
      >
        <Icon icon="i-lucide-ban" class="size-5 text-n-slate-11" />
      </span>
      <div class="flex flex-col gap-1">
        <h3 class="text-heading-2 text-n-slate-12">
          {{ $t('BLOCKED_CONTACTS.EMPTY.TITLE') }}
        </h3>
        <p class="max-w-sm text-body-main text-n-slate-11">
          {{ $t('BLOCKED_CONTACTS.EMPTY.DESCRIPTION') }}
        </p>
      </div>
    </div>

    <template v-else>
      <div class="flex flex-col divide-y divide-n-weak border-t border-n-weak">
        <div
          v-for="contact in contacts"
          :key="contact.id"
          class="flex items-center justify-between gap-4 py-4"
        >
          <div class="flex min-w-0 items-center gap-3">
            <Avatar
              :src="contact.thumbnail"
              :name="contact.name"
              :size="32"
              hide-offline-status
            />
            <div class="flex min-w-0 flex-col gap-0.5">
              <span class="block truncate text-body-main text-n-slate-12">
                {{ contact.name }}
              </span>
              <span
                v-if="contact.email || contact.phone_number"
                class="block truncate text-body-main text-n-slate-11"
              >
                {{ contact.email || contact.phone_number }}
              </span>
            </div>
          </div>
          <div class="flex shrink-0 items-center gap-3">
            <Button
              v-tooltip.top="$t('BLOCKED_CONTACTS.VIEW')"
              icon="i-lucide-eye"
              slate
              sm
              @click="$router.push(contactRoute(contact))"
            />
            <Button
              faded
              slate
              size="sm"
              :label="$t('BLOCKED_CONTACTS.UNBLOCK.BUTTON')"
              :is-loading="unblockingIds[contact.id]"
              @click="unblockContact(contact)"
            />
          </div>
        </div>
      </div>

      <div v-if="hasMore" class="flex justify-center pt-2">
        <Button
          ghost
          slate
          size="sm"
          :label="$t('BLOCKED_CONTACTS.LOAD_MORE')"
          :is-loading="isLoadingMore"
          @click="loadMore"
        />
      </div>
    </template>
  </SettingsPageShell>
</template>
